/**
 * ErrorPage — Componente base reutilizable para todas las páginas de error
 * Exporta: ErrorPage (named + default)
 * Invoca: Button (@/components/ui), lucide-react icons, react-router-dom
 * NO debe contener lógica de negocio ni datos reales.
 * NO debe depender de autenticación ni estado global.
 * Uso: Componer páginas de error específicas (404, 403, 500, etc.)
 */

import { ReactNode } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui";

interface ErrorPageAction {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost" | "outline";
}

interface ErrorPageProps {
  /** Código de error (404, 403, 500, etc.) */
  code: string;
  /** Título descriptivo del error */
  title: string;
  /** Descripción detallada para el usuario */
  description: string;
  /** Ícono o ilustración central */
  icon: ReactNode;
  /** Color de acento para el código (clase Tailwind text-*) */
  accentColor?: string;
  /** Acciones personalizadas. Si no se pasan, muestra las por defecto */
  actions?: ErrorPageAction[];
  /** Detalle técnico del error (solo visible en desarrollo) */
  errorDetail?: string;
  /** Mostrar botón de recarga */
  showReload?: boolean;
}

export const ErrorPage = ({
  code,
  title,
  description,
  icon,
  accentColor = "text-primary-600 dark:text-primary-400",
  actions,
  errorDetail,
  showReload = false,
}: ErrorPageProps) => {
  const resolvedActions = actions ?? [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-4 transition-colors duration-200">
      <div className="max-w-lg w-full text-center">
        {/* Ícono central */}
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 rounded-3xl bg-white dark:bg-slate-800 shadow-lg border border-gray-100 dark:border-slate-700 flex items-center justify-center">
            {icon}
          </div>
        </div>

        {/* Código de error */}
        <p
          className={`text-7xl sm:text-8xl font-extrabold tracking-tight ${accentColor} mb-2 select-none`}
        >
          {code}
        </p>

        {/* Título */}
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3">
          {title}
        </h1>

        {/* Descripción */}
        <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base leading-relaxed mb-8 max-w-md mx-auto">
          {description}
        </p>

        {/* Detalle técnico (solo dev) */}
        {errorDetail && (
          <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-gray-100 dark:border-slate-700 text-left text-xs font-mono text-red-600 dark:text-red-400 mb-6 max-h-28 overflow-auto shadow-sm">
            {errorDetail}
          </div>
        )}

        {/* Acciones */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          {resolvedActions.map((action, i) => (
            <Button
              key={i}
              variant={action.variant ?? "primary"}
              icon={action.icon}
              onClick={action.onClick}
              size="md"
            >
              {action.label}
            </Button>
          ))}
          {showReload && (
            <Button
              variant="ghost"
              icon={<RefreshCw size={18} />}
              onClick={() => window.location.reload()}
              size="md"
            >
              Recargar
            </Button>
          )}
        </div>

        {/* Sutil línea decorativa */}
        <div className="mt-12 flex items-center justify-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-slate-600" />
          <div className="w-1.5 h-1.5 rounded-full bg-gray-200 dark:bg-slate-700" />
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-slate-600" />
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
